import React, { useCallback, useEffect, useRef, useState } from 'react';
import clip from 'text-clipper';
import { View, colors, Heading } from '@fairygodboss/ui';
import window from 'global/window';
import { useEventRegistration } from '../EventRegistrationContext';
import { GridColumnWrapper } from './GridColumnWrapper';
import { ShowMoreMobileButton, ShowMoreButton } from './SpeakerSection';
import { isOverflown } from './helpers';
import { buildImagePath } from '../../../JobComponents/Company/Image/logo';

const Sponsor = ({ sponsor, isXs, sponsorsCount }) => {
    const { image, description, name } = sponsor;
    const [collapsed, setCollapsed] = useState(true);
    const [textLength, setTextLength] = useState(800);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [showGradient, setShowGradient] = useState(false);
    const textRef = useRef();
    const src = buildImagePath(image?.src, 'img', {
        img: 'c_scale,f_auto,q_auto,fl_lossy,w_270',
    });

    let clippedDescription = clip(description, textLength, {
        indicator: `...`,
    });

    if (!collapsed) {
        clippedDescription = description;
    }

    const onShowMoreClick = () => {
        setCollapsed(!collapsed);
    };

    const hasShortDescription = (descriptionText, threshold = 20) => {
        const words = descriptionText?.split(' ');
        return words?.length < threshold;
    };

    useEffect(() => {
        if (textRef?.current) {
            if (isOverflown(textRef.current)) {
                setHasOverflow(true);
            }
        }
    }, [textRef, isXs]);

    useEffect(() => {
        if (collapsed && isXs && hasOverflow) {
            setShowGradient(true);
        } else if (!collapsed && isXs) {
            setShowGradient(false);
        } else if (!isXs) {
            setShowGradient(false);
        }
    }, [collapsed, isXs, hasOverflow]);

    const handleResize = useCallback(() => {
        const width = window.innerWidth;
        if (width < 905) {
            return setTextLength(350);
        }
        if (width < 1240) {
            setTextLength(700);
        }
    }, []);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    return (
        <View
            display="flex"
            flexDirection={{ _: 'column', md: 'row' }}
            pb="32px"
        >
            <View
                display="flex"
                mr={{ _: '0px', md: '24px' }}
                mb={{ _: '24px', md: '0px' }}
                justifyContent={{
                    _: 'center',
                    md: 'inherit',
                    lg: hasShortDescription(description) ? 'inherit' : 'center',
                }}
                borderRadius="12px"
            >
                <View
                    borderRadius="12px"
                    width={{
                        _: '156px',
                        md: '197px',
                        lg: '270px',
                    }}
                    height={{
                        _: '156px',
                        md: '197px',
                        lg: '270px',
                    }}
                    boxShadow="0px 4px 8px rgba(53, 53, 53, 0.2)"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <View
                        width={{
                            _: '156px',
                            md: '197px',
                            lg: '270px',
                        }}
                        style={{
                            aspectRatio: '1/0',
                        }}
                        as="img"
                        src={src}
                        alt={image.alt}
                        borderRadius="12px"
                    />
                </View>
            </View>
            <View
                display={{ _: 'block', lg: 'flex' }}
                alignItems="flex-start"
                flexDirection="column"
            >
                {sponsorsCount !== 1 ? (
                    <Heading
                        pb={{ _: '24px', md: '40px' }}
                        variant={{
                            _: 'headingSm',
                        }}
                        fontWeight="700"
                        as="h2"
                    >
                        {name}
                    </Heading>
                ) : null}
                <View
                    ref={textRef}
                    maxHeight={{
                        _: collapsed ? '182px' : 'auto',
                        sm: 'inherit',
                    }}
                    overflow="hidden"
                    fontSize="16px"
                    fontWeight="400px"
                    lineHeight="26px"
                    css={{
                        WebkitMaskImage: showGradient
                            ? 'linear-gradient(180deg, #000 60%, transparent)'
                            : 'none',
                    }}
                >
                    {isXs ? description : null}
                    {!isXs ? clippedDescription : null}
                    {!isXs && collapsed && description?.length > textLength ? (
                        <ShowMoreButton onPress={onShowMoreClick} />
                    ) : null}
                </View>
            </View>
            {isXs && hasOverflow ? (
                <View display="flex" justifyContent="center">
                    <ShowMoreMobileButton
                        active={!collapsed}
                        onPress={onShowMoreClick}
                    />
                </View>
            ) : null}
        </View>
    );
};

export const Sponsors = () => {
    const { event, sponsors } = useEventRegistration();
    const { eventType, label } = event;
    const [isXs, setIsXs] = useState(window.innerWidth < 600);
    const handleResize = useCallback(() => {
        const _isXs = window.innerWidth < 600;
        setIsXs(_isXs);
    }, []);

    useEffect(() => {
        handleResize();
    }, [handleResize]);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    return sponsors.length && eventType === 'Webinar' ? (
        <GridColumnWrapper
            backgroundColor={colors.neutral[0]}
            gridAlignment="center"
            px={{ _: '16px', sm: '0px', md: '24px', lg: '0px' }}
            gridColumnSpacing={{ xs: 12, sm: 9, md: 10, lg: 10 }}
        >
            <View pt={{ _: '32px', md: '56px' }}>
                <Heading
                    pb={{ _: '24px', md: '40px' }}
                    variant={{
                        _: 'headingSm',
                        sm: 'headingMd',
                        md: 'headingLg',
                    }}
                    fontWeight="700"
                    as="h2"
                >
                    {`${label}${sponsors?.length > 1 ? ':' : ''} `}
                    {sponsors.length === 1 ? sponsors?.[0]?.name : ''}
                </Heading>
                {sponsors.map((s) => (
                    <Sponsor
                        sponsor={s}
                        isXs={isXs}
                        key={s?.name?.split(' ').join('-')}
                        sponsorsCount={sponsors.length}
                    />
                ))}
            </View>
        </GridColumnWrapper>
    ) : null;
};

Sponsors.displayName = 'Sponsors';
